interface GreenlightLogoProps {
  /** Height of the traffic-light housing in px (width scales proportionally 5:9). */
  size?: number;
  className?: string;
}

/**
 * Greenlight traffic-light logo.
 * Red and amber are dim; green is lit with a soft glow — "greenlight = go".
 */
export function GreenlightLogo({ size = 48, className }: GreenlightLogoProps) {
  const h = size;
  const w = Math.round(size * (40 / 72));

  return (
    <svg
      width={w}
      height={h}
      viewBox="0 0 40 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Greenlight logo"
    >
      <defs>
        {/* Green glow filter */}
        <filter id="gl-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Housing gradient */}
        <linearGradient id="gl-housing" x1="0" y1="0" x2="40" y2="72" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1e2433" />
          <stop offset="100%" stopColor="#111827" />
        </linearGradient>
      </defs>

      {/* Housing */}
      <rect x="0.75" y="0.75" width="38.5" height="70.5" rx="9.25" fill="url(#gl-housing)" />
      <rect x="0.75" y="0.75" width="38.5" height="70.5" rx="9.25" stroke="#2e3a50" strokeWidth="1.5" />

      {/* Mount bracket (bottom) */}
      <rect x="14" y="69" width="12" height="3" rx="1.5" fill="#1a2030" />

      {/* ── Red light (top) — dimmed ── */}
      <circle cx="20" cy="14" r="9" fill="#2a0f0f" />
      <circle cx="20" cy="14" r="6.5" fill="#4a1515" />
      {/* subtle inner reflection */}
      <circle cx="17.5" cy="11.5" r="1.5" fill="#6b2020" opacity="0.4" />

      {/* ── Amber light (middle) — dimmed ── */}
      <circle cx="20" cy="36" r="9" fill="#281f08" />
      <circle cx="20" cy="36" r="6.5" fill="#4a380d" />
      <circle cx="17.5" cy="33.5" r="1.5" fill="#6b5412" opacity="0.4" />

      {/* ── Green light (bottom) — ACTIVE ── */}
      {/* outer glow halo */}
      <circle cx="20" cy="58" r="9" fill="#052e16" opacity="0.8" filter="url(#gl-glow)" />
      {/* main lit circle */}
      <circle cx="20" cy="58" r="8" fill="#16a34a" filter="url(#gl-glow)" />
      {/* bright core */}
      <circle cx="20" cy="58" r="5.5" fill="#4ade80" />
      {/* specular highlight */}
      <circle cx="17.5" cy="55.5" r="2" fill="#bbf7d0" opacity="0.75" />
    </svg>
  );
}

/** Compact inline version for navbar (horizontal lockup). */
export function GreenlightLogomark({ size = 28, className }: GreenlightLogoProps) {
  return <GreenlightLogo size={size} className={className} />;
}
